import * as React from "react"
import { router } from "@inertiajs/react"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
} from "@/components/ui/Dialog"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import {
  team_member_path,
  deactivate_team_member_path,
  reactivate_team_member_path,
} from "@/lib/routes"

// Inertia types callback errors as string values, but our Rails controller
// shares `model.errors.messages` (arrays), so accept both shapes.
type Errors = Record<string, string | string[]>

type Props = {
  member: { user_id: string; name: string; slack_handle: string | null; active: boolean }
  // Deactivate is separate from the edit permission that opens this dialog.
  canDeactivate: boolean
  // Hide the deactivate action on your own account (matches the server self-guard).
  isSelf: boolean
}

export default function EditMemberDialog({ member, canDeactivate, isSelf }: Props) {
  const [open, setOpen] = React.useState(false)
  const [name, setName] = React.useState(member.name)
  const [slackHandle, setSlackHandle] = React.useState(member.slack_handle ?? "")
  const [submitting, setSubmitting] = React.useState(false)
  const [errors, setErrors] = React.useState<Errors>({})

  const showLifecycle = canDeactivate && !isSelf

  const toggleActive = () => {
    const message = member.active
      ? `Deactivate ${member.name}? They will be signed out and unable to log in. Their role assignments are kept, so you can reactivate them later to restore access.`
      : `Reactivate ${member.name}? They will be able to log in again with their previous role assignments.`
    if (!confirm(message)) return

    setSubmitting(true)
    const path = member.active
      ? deactivate_team_member_path(member.user_id)
      : reactivate_team_member_path(member.user_id)
    router.patch(path, {}, {
      onFinish: () => setSubmitting(false),
      onSuccess: () => setOpen(false),
    })
  }

  const reset = () => {
    setName(member.name)
    setSlackHandle(member.slack_handle ?? "")
    setErrors({})
    setSubmitting(false)
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setErrors({})
    router.patch(
      team_member_path(member.user_id),
      { user: { name, slack_handle: slackHandle } },
      {
        onError: (errs) => setErrors(errs),
        onFinish: () => setSubmitting(false),
        onSuccess: () => setOpen(false),
      }
    )
  }

  const fieldError = (field: string) => {
    const msgs = errors[field]
    if (!msgs) return null
    return (
      <span className="text-xs text-destructive">
        {Array.isArray(msgs) ? msgs.join(", ") : msgs}
      </span>
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (o) reset()
      }}
    >
      <DialogTrigger asChild>
        <button type="button" className="text-link hover:underline cursor-pointer text-left">
          {member.name}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit member</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`edit-member-name-${member.user_id}`} className="text-sm font-medium">
              Name
            </label>
            <Input
              id={`edit-member-name-${member.user_id}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              autoFocus
            />
            {fieldError("name")}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={`edit-member-slack-${member.user_id}`}
              className="text-sm font-medium"
            >
              Slack handle
            </label>
            <Input
              id={`edit-member-slack-${member.user_id}`}
              value={slackHandle}
              onChange={(e) => setSlackHandle(e.target.value)}
              placeholder="jane.doe"
              disabled={submitting}
            />
            {fieldError("slack_handle")}
          </div>

          <DialogFooter className={showLifecycle ? "justify-between" : undefined}>
            {showLifecycle && (
              <button
                type="button"
                onClick={toggleActive}
                disabled={submitting}
                className={
                  member.active
                    ? "text-sm text-destructive hover:underline cursor-pointer disabled:opacity-50"
                    : "text-sm text-link hover:underline cursor-pointer disabled:opacity-50"
                }
              >
                {member.active ? "Deactivate" : "Reactivate"}
              </button>
            )}
            <div className="flex gap-2">
              <DialogClose asChild>
                <Button type="button" variant="ghost" disabled={submitting}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Saving…" : "Save"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
